import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { useAdminScope } from '@/hooks/useAdminScope';
import { NewOrganizationDialog } from '@/components/super-admin/NewOrganizationDialog';
import { ADMIN_ORG_PACKAGE_SELECT } from '@/lib/billing/customerBillingSelectors';
import {
  ADMIN_PAGE_CONTAINER, ADMIN_SECTION_SPACING, ADMIN_PAGE_TITLE,
  ADMIN_PAGE_HEADER, ADMIN_TABLE_WRAPPER,
} from '@/lib/adminResponsive';
import type { Tables } from '@/integrations/supabase/types';

type OrganizationRow = Pick<Tables<'organizations'>, 'id' | 'name' | 'industry' | 'lifecycle_status' | 'created_at'>;

const errorMessage = (error: unknown) => error instanceof Error ? error.message : String(error);

export default function SAOrganizations() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { scope } = useAdminScope();
  const canCreateOrganization = scope === 'super_admin' || scope === 'finance_admin';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationRow | null>(null);
  const [form, setForm] = useState({ plan_id: '', status: 'active', starts_at: '', expires_at: '', admin_reason: '' });

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['sa-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name, industry, lifecycle_status, created_at').order('created_at');
      if (error) throw error;
      return data;
    },
  });

  const { data: subs = [] } = useQuery({
    queryKey: ['sa-all-subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organization_subscriptions').select(ADMIN_ORG_PACKAGE_SELECT).order('starts_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['sa-plans'],
    queryFn: async () => {
      const { data, error } = await supabase.from('plans').select('*').eq('is_active', true).eq('platform', 'forkhire').order('created_at');
      if (error) throw error;
      return data;
    },
  });

  const { data: planServices = [] } = useQuery({
    queryKey: ['sa-all-plan-services'],
    queryFn: async () => {
      const { data, error } = await supabase.from('plan_services').select('*, services(service_name)').eq('is_enabled', true);
      if (error) throw error;
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (form.starts_at && form.expires_at && form.expires_at <= form.starts_at) {
        throw new Error(t('admin.organizations.expiryAfterStart'));
      }

      if (!selectedOrg) throw new Error(t('admin.organizations.updatePackageFailed'));
      const { data, error } = await supabase.rpc('grant_forkhire_organization_package', {
        p_org_id: selectedOrg.id,
        p_plan_id: form.plan_id,
        p_status: form.status,
        p_starts_at: form.starts_at || new Date().toISOString(),
        p_ends_at: form.expires_at || null,
        p_reason: form.admin_reason.trim(),
      });
      if (error) throw error;
      if (data && typeof data === 'object' && !Array.isArray(data) && data.success === false) {
        throw new Error(typeof data.error === 'string' ? data.error : t('admin.organizations.updatePackageFailed'));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-all-subscriptions'] });
      qc.invalidateQueries({ queryKey: ['sa-organizations'] });
      setDialogOpen(false);
      toast({ title: t('admin.organizations.subscriptionUpdated') });
    },
    onError: (error: unknown) => toast({ title: t('common.error'), description: errorMessage(error), variant: 'destructive' }),
  });

  const openAssign = (org: OrganizationRow) => {
    setSelectedOrg(org);
    const activeSub = subs.find((s) => s.org_id === org.id && s.status === 'active');
    setForm({
      plan_id: activeSub?.plan_id || '', status: activeSub?.status || 'active',
      starts_at: activeSub?.starts_at ? activeSub.starts_at.slice(0, 10) : '',
      expires_at: activeSub?.expires_at ? activeSub.expires_at.slice(0, 10) : '',
      admin_reason: '',
    });
    setDialogOpen(true);
  };

  const getOrgSub = (orgId: string) => subs.find((s) => s.org_id === orgId && s.status === 'active');
  const getEnabledServices = (planId: string) => planServices
    .filter((planService) => planService.plan_id === planId)
    .map((planService) => planService.services?.service_name)
    .filter((serviceName): serviceName is string => typeof serviceName === 'string');
  const dateRangeError = form.starts_at && form.expires_at && form.expires_at <= form.starts_at
    ? t('admin.organizations.expiryAfterStart')
    : '';

  return (
    <div className={`${ADMIN_SECTION_SPACING} ${ADMIN_PAGE_CONTAINER}`}>
      <div className={ADMIN_PAGE_HEADER}>
        <h1 className={ADMIN_PAGE_TITLE}>{t('admin.organizations.title')}</h1>
        {canCreateOrganization && <NewOrganizationDialog />}
      </div>

      <div className={ADMIN_TABLE_WRAPPER}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.organizations.organization')}</TableHead>
              <TableHead>{t('admin.organizations.industry')}</TableHead>
              <TableHead>{t('admin.organizations.currentPlan')}</TableHead>
              <TableHead>{t('admin.table.status')}</TableHead>
              <TableHead>{t('admin.organizations.start')}</TableHead>
              <TableHead>{t('admin.organizations.expiry')}</TableHead>
              <TableHead>{t('admin.organizations.enabledServices')}</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : orgs.map((org) => {
              const sub = getOrgSub(org.id);
              const enabledSvcs = sub ? getEnabledServices(sub.plan_id) : [];
              return (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <Link to={`/super-admin/organizations/${org.id}`} className="text-primary hover:underline">{org.name}</Link>
                    {org.lifecycle_status && org.lifecycle_status !== 'active' && (
                      <Badge variant="destructive" className="ms-2 text-xs">{org.lifecycle_status.replace(/_/g, ' ')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{org.industry || '—'}</TableCell>
                  <TableCell>
                    {sub ? <Badge variant="outline">{sub.plans?.plan_name || t('admin.common.notAvailable')}</Badge> : <span className="text-muted-foreground text-sm">{t('admin.organizations.noPlan')}</span>}
                  </TableCell>
                  <TableCell>
                    {sub ? <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>{t(`admin.status.${sub.status}`, sub.status)}</Badge> : '—'}
                  </TableCell>
                  <TableCell className="text-sm">{sub?.starts_at ? formatLocalizedDate(sub.starts_at, 'MMM d, yyyy', i18n.language) : t('admin.common.notAvailable')}</TableCell>
                  <TableCell className="text-sm">{sub?.expires_at ? formatLocalizedDate(sub.expires_at, 'MMM d, yyyy', i18n.language) : t('admin.common.notAvailable')}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {enabledSvcs.length > 0 ? enabledSvcs.map((s: string) => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>) : <span className="text-muted-foreground text-xs">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openAssign(org)}><Pencil className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.organizations.assignPlan')} — {selectedOrg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('admin.organizations.plan')}</Label>
              <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t('admin.organizations.selectPlan')} /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.plan_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.table.status')}</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('admin.status.active')}</SelectItem>
                  <SelectItem value="suspended">{t('admin.status.suspended')}</SelectItem>
                  <SelectItem value="expired">{t('admin.status.expired')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('admin.organizations.startDate')}</Label><Input type="date" value={form.starts_at} onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))} /></div>
              <div><Label>{t('admin.organizations.expiryDate')}</Label><Input type="date" min={form.starts_at || undefined} value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} aria-invalid={!!dateRangeError} /></div>
            </div>
            {dateRangeError && <p className="text-sm font-medium text-destructive">{dateRangeError}</p>}
            <div>
              <Label>{t('admin.organizationDetail.adminReason')}</Label>
              <Textarea
                value={form.admin_reason}
                onChange={(e) => setForm((f) => ({ ...f, admin_reason: e.target.value }))}
                placeholder={t('admin.organizationDetail.planReasonPlaceholder')}
                className="min-h-[96px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.plan_id || !!dateRangeError || !form.admin_reason.trim()}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
